/**
 * CONCIERGE-01: Admin Settings Panel (tab in Administration.jsx)
 * Tenant admin controls for: widget settings, flow management, branding,
 * WhatsApp handoff config, routing rules, analytics.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { MessageCircle, Plus, Eye, Edit3, Upload, Trash2, BarChart3, Settings, RefreshCw, Check, X } from "lucide-react";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL + "/api";

const api = (path, method = "GET", data) =>
  axios({ method, url: `${BASE}${path}`, data, withCredentials: true }).then(r => r.data);

function StatCard({ label, value, sub }) {
  return (
    <div style={{ background: "#fdf8f0", border: "1px solid #e8dfc9", borderRadius: "12px", padding: "16px 20px" }}>
      <div style={{ fontSize: "24px", fontWeight: "700", color: "#9a5c1a" }}>{value}</div>
      <div style={{ fontSize: "13px", color: "#374151", marginTop: "2px" }}>{label}</div>
      {sub && <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>{sub}</div>}
    </div>
  );
}

function FlowCard({ flow, onPublish, onDuplicate, onDelete, onEdit }) {
  const statusColors = {
    PUBLISHED: { bg: "#dcfce7", color: "#15803d" },
    DRAFT: { bg: "#fef9c3", color: "#854d0e" },
    ARCHIVED: { bg: "#f3f4f6", color: "#6b7280" },
  };
  const sc = statusColors[flow.status] || statusColors.DRAFT;
  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: "12px", padding: "16px", background: "#fff" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
            <span style={{ fontWeight: "600", fontSize: "14px", color: "#1f2937" }}>{flow.name}</span>
            <span style={{ padding: "1px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: "600", background: sc.bg, color: sc.color }}>{flow.status}</span>
          </div>
          <div style={{ fontSize: "12px", color: "#6b7280" }}>
            v{flow.version} · {(flow.questions || []).length} questions · {flow.language || "en"}
          </div>
          {flow.published_at && (
            <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "3px" }}>Published {new Date(flow.published_at).toLocaleDateString()}</div>
          )}
        </div>
        <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
          {flow.status === "DRAFT" && (
            <button
              data-testid={`flow-publish-${flow.flow_id}`}
              onClick={() => onPublish(flow.flow_id)}
              style={{ padding: "5px 12px", borderRadius: "8px", background: "#C9A15F", border: "none", color: "#fff", fontSize: "12px", cursor: "pointer", fontWeight: "600" }}
            >
              Publish
            </button>
          )}
          <button
            onClick={() => onDuplicate(flow.flow_id)}
            title="Duplicate"
            style={{ padding: "5px 8px", borderRadius: "8px", background: "#f3f4f6", border: "none", cursor: "pointer", color: "#6b7280" }}
          >
            <Edit3 size={14} />
          </button>
          {flow.status === "DRAFT" && (
            <button
              onClick={() => onDelete(flow.flow_id)}
              title="Delete draft"
              style={{ padding: "5px 8px", borderRadius: "8px", background: "#fee2e2", border: "none", cursor: "pointer", color: "#b91c1c" }}
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SettingsForm({ settings, onSave }) {
  const [form, setForm] = useState({
    enabled: settings?.enabled ?? true,
    show_teaser: settings?.show_teaser ?? true,
    teaser_text: settings?.teaser_text || "Need help planning your event?",
    launcher_position: settings?.launcher_position || "bottom-right",
    offline_message: settings?.offline_message || "",
    visible_on_pages: settings?.visible_on_pages || ["landing", "build-my-event", "public-inquiry"],
    session_ttl_hours: settings?.session_ttl_hours || 720,
    rate_limit_per_ip_per_hour: settings?.rate_limit_per_ip_per_hour || 10,
  });
  const [saving, setSaving] = useState(false);
  const PAGE_OPTIONS = [
    { id: "landing", label: "Landing / Home Page" },
    { id: "build-my-event", label: "Build My Event Journey" },
    { id: "public-inquiry", label: "Public Inquiry Form" },
  ];
  const togglePage = (pageId) => {
    setForm(f => ({
      ...f,
      visible_on_pages: f.visible_on_pages.includes(pageId)
        ? f.visible_on_pages.filter(p => p !== pageId)
        : [...f.visible_on_pages, pageId]
    }));
  };
  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form);
      toast.success("Settings saved");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px", padding: "16px 0" }}>
      {/* Enable toggle */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <label style={{ fontSize: "14px", color: "#374151", fontWeight: "500", flex: 1 }}>Enable Concierge Widget</label>
        <button
          data-testid="concierge-toggle-enabled"
          onClick={() => setForm(f => ({ ...f, enabled: !f.enabled }))}
          style={{
            width: "48px", height: "26px", borderRadius: "13px",
            background: form.enabled ? "#C9A15F" : "#d1d5db",
            border: "none", cursor: "pointer", position: "relative", transition: "background 0.2s"
          }}
        >
          <span style={{
            position: "absolute", top: "3px",
            left: form.enabled ? "25px" : "3px",
            width: "20px", height: "20px", borderRadius: "50%",
            background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
          }} />
        </button>
      </div>

      {/* Page visibility */}
      <div>
        <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "8px", fontWeight: "500" }}>Show on pages</div>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {PAGE_OPTIONS.map(p => (
            <label key={p.id} style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", fontSize: "13px", color: "#374151" }}>
              <input
                type="checkbox"
                checked={form.visible_on_pages.includes(p.id)}
                onChange={() => togglePage(p.id)}
                style={{ accentColor: "#C9A15F" }}
              />
              {p.label}
            </label>
          ))}
        </div>
      </div>

      {/* Teaser */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <label style={{ fontSize: "14px", color: "#374151", fontWeight: "500", flex: 1 }}>Show Welcome Teaser</label>
        <input type="checkbox" checked={form.show_teaser} onChange={e => setForm(f => ({ ...f, show_teaser: e.target.checked }))} style={{ accentColor: "#C9A15F", width: "16px", height: "16px" }} />
      </div>
      {form.show_teaser && (
        <div>
          <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "4px" }}>Teaser Text</div>
          <input
            value={form.teaser_text}
            onChange={e => setForm(f => ({ ...f, teaser_text: e.target.value }))}
            style={{ width: "100%", padding: "8px 12px", borderRadius: "8px", border: "1px solid #d1d5db", fontSize: "13px" }}
            placeholder="Need help planning your event?"
          />
        </div>
      )}

      {/* Position */}
      <div>
        <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "4px", fontWeight: "500" }}>Launcher Position</div>
        <div style={{ display: "flex", gap: "8px" }}>
          {["bottom-right", "bottom-left"].map(pos => (
            <button
              key={pos}
              onClick={() => setForm(f => ({ ...f, launcher_position: pos }))}
              style={{
                padding: "6px 14px", borderRadius: "8px", fontSize: "12px",
                border: "1.5px solid " + (form.launcher_position === pos ? "#C9A15F" : "#d1d5db"),
                background: form.launcher_position === pos ? "#fdf6ec" : "#fff",
                color: form.launcher_position === pos ? "#9a7133" : "#6b7280",
                cursor: "pointer", fontWeight: form.launcher_position === pos ? "600" : "400"
              }}
            >
              {pos === "bottom-right" ? "Bottom Right" : "Bottom Left"}
            </button>
          ))}
        </div>
      </div>

      <button
        data-testid="concierge-settings-save"
        onClick={handleSave}
        disabled={saving}
        style={{
          alignSelf: "flex-start", padding: "10px 24px", borderRadius: "10px",
          background: saving ? "#e0d5c0" : "#C9A15F", border: "none",
          color: "#fff", fontSize: "14px", cursor: saving ? "not-allowed" : "pointer", fontWeight: "600"
        }}
      >
        {saving ? "Saving…" : "Save Settings"}
      </button>
    </div>
  );
}

export default function ConciergeAdminPanel() {
  const [activeTab, setActiveTab] = useState("overview");
  const [flows, setFlows] = useState([]);
  const [settings, setSettings] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [flowsRes, settingsRes, analyticsRes] = await Promise.all([
        api("/concierge/flows"),
        api("/concierge/settings"),
        api("/concierge/analytics/summary"),
      ]);
      setFlows(flowsRes.flows || []);
      setSettings(settingsRes);
      setAnalytics(analyticsRes);
    } catch (e) {
      toast.error("Failed to load concierge data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePublish = async (flowId) => {
    try {
      await api(`/concierge/flows/${flowId}/publish`, "POST");
      toast.success("Flow published");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to publish");
    }
  };
  const handleDuplicate = async (flowId) => {
    try {
      await api(`/concierge/flows/${flowId}/duplicate`, "POST");
      toast.success("Flow duplicated as draft");
      load();
    } catch (e) {
      toast.error("Failed to duplicate");
    }
  };
  const handleDelete = async (flowId) => {
    if (!window.confirm("Delete this draft flow?")) return;
    try {
      await api(`/concierge/flows/${flowId}`, "DELETE");
      toast.success("Draft deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };
  const handleSeedDefault = async () => {
    try {
      const r = await api("/concierge/flows/seed-default", "POST");
      if (r.seeded) { toast.success("Default flow seeded and published"); load(); }
      else toast.info(r.reason || "Flow already exists");
    } catch (e) {
      toast.error("Failed to seed default flow");
    }
  };
  const handleSaveSettings = async (form) => {
    await api("/concierge/settings", "PATCH", form);
    load();
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "flows", label: "Flows", icon: MessageCircle },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  if (loading) return <div style={{ padding: "32px", textAlign: "center", color: "#9ca3af", fontSize: "14px" }}>Loading concierge configuration…</div>;

  return (
    <div data-testid="concierge-admin-panel" style={{ fontFamily: "'DM Sans', sans-serif", padding: "0" }}>
      {/* Sub-tabs */}
      <div style={{ display: "flex", gap: "2px", marginBottom: "20px", background: "#f9fafb", borderRadius: "10px", padding: "4px" }}>
        {tabs.map(t => (
          <button
            key={t.id}
            data-testid={`concierge-tab-${t.id}`}
            onClick={() => setActiveTab(t.id)}
            style={{
              flex: 1, padding: "8px 12px", borderRadius: "8px", border: "none",
              background: activeTab === t.id ? "#fff" : "transparent",
              color: activeTab === t.id ? "#9a7133" : "#6b7280",
              fontWeight: activeTab === t.id ? "600" : "400",
              cursor: "pointer", fontSize: "13px", display: "flex", alignItems: "center",
              justifyContent: "center", gap: "6px",
              boxShadow: activeTab === t.id ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
              fontFamily: "inherit",
            }}
          >
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {activeTab === "overview" && analytics && (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px" }}>
            <StatCard label="Total Sessions" value={analytics.total_sessions} />
            <StatCard label="Submitted" value={analytics.submitted} />
            <StatCard label="Conversion" value={`${analytics.conversion_rate}%`} />
            <StatCard label="Active" value={analytics.active} />
            <StatCard label="Abandoned" value={analytics.abandoned} />
            <StatCard label="Completed" value={analytics.completed} />
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button onClick={load} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px", borderRadius: "8px", background: "#f3f4f6", border: "none", cursor: "pointer", fontSize: "13px", color: "#374151" }}>
              <RefreshCw size={14} />Refresh
            </button>
          </div>
        </div>
      )}

      {/* Flows */}
      {activeTab === "flows" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: "14px", color: "#6b7280" }}>{flows.length} flow{flows.length !== 1 ? "s" : ""}</div>
            <div style={{ display: "flex", gap: "8px" }}>
              {flows.length === 0 && (
                <button
                  data-testid="concierge-seed-default"
                  onClick={handleSeedDefault}
                  style={{ padding: "8px 16px", borderRadius: "8px", background: "#C9A15F", border: "none", color: "#fff", fontSize: "13px", cursor: "pointer", fontWeight: "600" }}
                >
                  Seed Default Flow
                </button>
              )}
            </div>
          </div>
          {flows.length === 0 ? (
            <div style={{ padding: "32px", textAlign: "center", color: "#9ca3af", border: "2px dashed #e5e7eb", borderRadius: "12px" }}>
              <MessageCircle size={28} style={{ margin: "0 auto 10px", opacity: 0.4 }} />
              <div style={{ fontSize: "14px", marginBottom: "4px" }}>No conversation flows yet</div>
              <div style={{ fontSize: "12px" }}>Seed the default Prashasta event-enquiry flow to get started</div>
            </div>
          ) : (
            flows.map(flow => (
              <FlowCard
                key={flow.flow_id}
                flow={flow}
                onPublish={handlePublish}
                onDuplicate={handleDuplicate}
                onDelete={handleDelete}
              />
            ))
          )}
        </div>
      )}

      {/* Settings */}
      {activeTab === "settings" && (
        <SettingsForm settings={settings} onSave={handleSaveSettings} />
      )}
    </div>
  );
}
